import { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";

// Animação para a "Melhor Opção"
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(29, 185, 84, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(29, 185, 84, 0); }
  100% { box-shadow: 0 0 0 0 rgba(29, 185, 84, 0); }
`;

export default function ComparadorCombustivel() {
  const [distance, setDistance] = useState("");
  const [fuels, setFuels] = useState({
    etanol: { selected: true, price: "", consumption: "" },
    etanolAditivado: { selected: false, price: "", consumption: "" },
    gasolina: { selected: true, price: "", consumption: "" },
    gasolinaAditivada: { selected: false, price: "", consumption: "" },
    gnv: { selected: false, price: "", consumption: "" }
  });

  const [results, setResults] = useState({});
  const [best, setBest] = useState("");

  useEffect(() => {
    calculate();
  }, [distance, fuels]);

  function formatCurrency(value) {
    let numbers = value.replace(/\D/g, "");
    numbers = numbers.padStart(3, "0");
    let integer = numbers.slice(0, -2);
    let decimal = numbers.slice(-2);
    return `${parseInt(integer)}.${decimal}`;
  }

  function handleMaskedInput(value, callback) {
    const formatted = formatCurrency(value);
    callback(formatted);
  }

  function calculate() {
    let d = parseFloat(distance);
    if (!d) return;

    let newResults = {};
    let lowest = Infinity;
    let bestFuel = "";

    Object.entries(fuels).forEach(([key, data]) => {
      if (data.selected) {
        let price = parseFloat(data.price);
        let consumption = parseFloat(data.consumption);

        if (price && consumption) {
          let cost = (d / consumption) * price;
          newResults[key] = cost;
          if (cost < lowest) {
            lowest = cost;
            bestFuel = key;
          }
        }
      }
    });

    setResults(newResults);
    setBest(bestFuel);
  }

  function handleChange(type, field, value) {
    setFuels({
      ...fuels,
      [type]: { ...fuels[type], [field]: value }
    });
  }

  function formatName(key) {
    const names = {
      etanol: "Etanol",
      etanolAditivado: "Etanol Aditiv.",
      gasolina: "Gasolina",
      gasolinaAditivada: "Gasolina Aditiv.",
      gnv: "GNV"
    };
    return names[key];
  }

  return (
    <Container>
      <header>
        <h1>Comparador de <span>Combustível</span></h1>
        <p>Descubra qual opção rende mais no seu bolso.</p>
      </header>

      <HeaderInputCard>
        <Field>
          <label>Distância da Viagem</label>
          <div className="input-wrapper">
            <input
              type="text"
              placeholder="0.00"
              value={distance}
              onChange={(e) => handleMaskedInput(e.target.value, setDistance)}
              inputMode="numeric"
            />
            <span>km</span>
          </div>
        </Field>
      </HeaderInputCard>

      <Grid>
        {Object.entries(fuels).map(([key, data]) => (
          <Card key={key} isActive={data.selected} isBest={best === key}>
            <TopRow>
              <CheckboxContainer>
                <input
                  type="checkbox"
                  checked={data.selected}
                  onChange={e => handleChange(key, "selected", e.target.checked)}
                />
                <div className="checkmark"></div>
              </CheckboxContainer>
              <h3>{formatName(key)}</h3>
            </TopRow>

            <CardContent visible={data.selected}>
              <Field>
                <label>Consumo Médio</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    placeholder="0.00"
                    value={data.consumption}
                    onChange={(e) => handleMaskedInput(e.target.value, (val) => handleChange(key, "consumption", val))}
                    inputMode="numeric"
                  />
                  <span>km/l</span>
                </div>
              </Field>

              <Field>
                <label>Preço do Litro</label>
                <div className="input-wrapper">
                  <span>R$</span>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={data.price}
                    onChange={(e) => handleMaskedInput(e.target.value, (val) => handleChange(key, "price", val))}
                    inputMode="numeric"
                  />
                </div>
              </Field>

              {results[key] > 0 && (
                <ResultArea>
                  <label>Custo Total</label>
                  <div className="price">R$ {results[key].toFixed(2)}</div>
                </ResultArea>
              )}

              {best === key && distance > 0 && (
                <BestBadge>Melhor Escolha</BestBadge>
              )}
            </CardContent>
          </Card>
        ))}
      </Grid>
    </Container>
  );
}

/* ESTILO */

const Container = styled.div`
  padding: 40px 20px;
  background: radial-gradient(circle at top right, #1a1a1a, #0a0a0a);
  color: #fff;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;

  header {
    text-align: center;
    margin-bottom: 40px;
    
    h1 {
      font-size: 2.5rem;
      margin-bottom: 8px;
      span { color: #ff1a1a; }
    }
    p { color: #888; font-size: 0.95rem; }
  }
`;

const HeaderInputCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  max-width: 400px;
  margin: 0 auto 50px;
  padding: 24px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: ${props => props.isActive ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.02)"};
  padding: 24px;
  border-radius: 24px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid ${props => props.isBest ? "#1db954" : "rgba(255, 255, 255, 0.08)"};
  position: relative;
  overflow: hidden;

  ${props => props.isBest && css`
    animation: ${pulse} 2s infinite;
    background: rgba(29, 185, 84, 0.03);
  `}

  &:hover {
    transform: translateY(-5px);
    border-color: ${props => props.isBest ? "#1db954" : "rgba(255, 26, 26, 0.4)"};
  }
`;

const CardContent = styled.div`
  opacity: ${props => props.visible ? "1" : "0.3"};
  pointer-events: ${props => props.visible ? "all" : "none"};
  transition: 0.3s;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
  }
`;

const Field = styled.div`
  margin-bottom: 18px;

  label {
    display: block;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #888;
    margin-bottom: 8px;
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    background: #000;
    padding: 2px 12px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: 0.2s;

    &:focus-within {
      border-color: #ff1a1a;
    }

    input {
      background: transparent;
      border: none;
      color: #fff;
      padding: 12px 8px;
      width: 100%;
      font-size: 1rem;
      outline: none;
      
      &::placeholder { color: #444; }
    }

    span {
      color: #555;
      font-size: 0.85rem;
      font-weight: bold;
    }
  }
`;

const ResultArea = styled.div`
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px dashed rgba(255, 255, 255, 0.1);

  .price {
    font-size: 1.8rem;
    font-weight: 800;
    color: #fff;
  }
`;

const BestBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: #1db954;
  color: #000;
  font-size: 0.65rem;
  font-weight: 900;
  padding: 4px 10px;
  border-radius: 8px;
  text-transform: uppercase;
`;

const CheckboxContainer = styled.label`
  position: relative;
  cursor: pointer;
  display: block;
  width: 24px;
  height: 24px;

  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  .checkmark {
    height: 24px;
    width: 24px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    transition: 0.2s;

    &:after {
      content: "";
      position: absolute;
      display: none;
      left: 9px;
      top: 5px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  input:checked ~ .checkmark {
    background: #ff1a1a;
    &:after { display: block; }
  }
`;